import { useEffect, useState } from "react";
import type { ResolvedPlacement, TargetRect, TourPlacement } from "./types";

const GAP = 30;
const CARD_WIDTH = 320;
const DEFAULT_CARD_HEIGHT = 100;
const VIEWPORT_PAD = 16;
const TOP_INSET = 4;

export function usePlacement(
  rect: TargetRect | null,
  preferred: TourPlacement,
  cardHeight: number = DEFAULT_CARD_HEIGHT
): ResolvedPlacement | null {
  const [resolved, setResolved] = useState<ResolvedPlacement | null>(null);

  useEffect(() => {
    if (!rect) {
      setResolved(null);
      return;
    }
    setResolved(computePlacement(rect, preferred, cardHeight));
  }, [rect, preferred, cardHeight]);

  return resolved;
}

function computePlacement(
  rect: TargetRect,
  preferred: TourPlacement,
  cardHeight: number
): ResolvedPlacement {
  const placement = flipIfOffscreen(rect, preferred, cardHeight);
  const { cardLeft, cardTop } = positionForPlacement(
    rect,
    placement,
    cardHeight
  );
  return { placement, cardLeft, cardTop };
}

function flipIfOffscreen(
  rect: TargetRect,
  preferred: TourPlacement,
  cardHeight: number
): TourPlacement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (
    preferred === "bottom" &&
    rect.top + rect.height + GAP + cardHeight > vh
  ) {
    return "top";
  }
  if (preferred === "top" && rect.top - GAP - cardHeight < 0) {
    return "bottom";
  }
  if (preferred === "right" && rect.left + rect.width + GAP + CARD_WIDTH > vw) {
    return "left";
  }
  if (preferred === "left" && rect.left - GAP - CARD_WIDTH < 0) {
    return "right";
  }
  return preferred;
}

function positionForPlacement(
  rect: TargetRect,
  placement: TourPlacement,
  cardHeight: number
): { cardLeft: number; cardTop: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerX = rect.left + rect.width / 2 - CARD_WIDTH / 2;
  const topAligned = rect.top + TOP_INSET;

  let cardLeft = 0;
  let cardTop = 0;

  if (placement === "bottom") {
    cardLeft = centerX;
    cardTop = rect.top + rect.height + GAP;
  } else if (placement === "top") {
    cardLeft = centerX;
    cardTop = rect.top - GAP - cardHeight;
  } else if (placement === "right") {
    cardLeft = rect.left + rect.width + GAP;
    cardTop = topAligned;
  } else {
    cardLeft = rect.left - GAP - CARD_WIDTH;
    cardTop = topAligned;
  }

  return {
    cardLeft: clamp(cardLeft, VIEWPORT_PAD, vw - CARD_WIDTH - VIEWPORT_PAD),
    cardTop: clamp(cardTop, VIEWPORT_PAD, vh - cardHeight - VIEWPORT_PAD),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export const TOUR_CARD_WIDTH = CARD_WIDTH;
